import React from "react";
import { Avatar, Typography, Box, Stack } from "@mui/material";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import WcIcon from "@mui/icons-material/Wc";
import CalendarIcon from "@mui/icons-material/CalendarMonthOutlined";
import HomeIcon from "@mui/icons-material/Home";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import { useAuth } from "../../context/AuthContext";
import { DateTime } from "luxon";
import { Global } from "@emotion/react";

const AccountSummaryCard = () => {
  const { user, isAuthenticated } = useAuth();
  if (!isAuthenticated || !user) return null;

  const profile = user.profile;
  const fullName = `${profile?.firstName || ""} ${
    profile?.lastName || ""
  }`.trim();
  const phone = profile?.phoneNumber;
  const gender = profile?.gender;
  const address = profile?.address?.fullAddress;
  const dob = profile?.dob;

  const formattedDob = dob
    ? DateTime.fromISO(dob).toLocaleString(DateTime.DATE_MED)
    : null;

  const getDisplayValue = (value) => {
    if (!value || value.trim() === "" || value.toLowerCase() === "unknown") {
      return "Not provided";
    }
    return value;
  };

  const isProvided = (value) => {
    if (!value || value.trim() === "" || value.toLowerCase() === "unknown") {
      return false;
    }
    return true;
  };

  const InfoRow = ({ icon, value }) => {
    const displayValue = getDisplayValue(value);
    const provided = isProvided(value);

    return (
      <Stack direction="row" spacing={1} alignItems="center">
        {icon}
        {!provided && (
          <InsertDriveFileOutlinedIcon
            fontSize="small"
            sx={{
              color: "#ccc",
              opacity: 0,
              animation: "fadeBounceInPlaceholder 0.8s ease forwards",
            }}
          />
        )}
        <Typography
          variant="body2"
          sx={{
            color: provided ? "text.secondary" : "#aaa",
            fontStyle: provided ? "normal" : "italic",
            opacity: provided ? 1 : 0,
            animation: provided
              ? "none"
              : "fadeBounceInPlaceholder 0.8s ease forwards",
          }}>
          {displayValue}
        </Typography>
      </Stack>
    );
  };

  return (
    <>
      <Global
        styles={`
          @keyframes fadeBounceInPlaceholder {
            0% {
              opacity: 0;
              transform: translateY(-5px) scale(0.98);
            }
            50% {
              opacity: 0.5;
              transform: translateY(1px) scale(1.02);
            }
            100% {
              opacity: 0.8;
              transform: translateY(0) scale(1);
            }
          }
        `}
      />

      <Stack direction="row" spacing={2} alignItems="flex-start">
        <Avatar sx={{ width: 56, height: 56 }}>
          {profile?.firstName?.[0]?.toUpperCase() || "U"}
        </Avatar>

        <Box>
          <Typography fontWeight="bold" sx={{ mb: 0.5 }}>
            {fullName || "Unknown"}
          </Typography>

          <Stack spacing={0.75}>
            <InfoRow
              icon={<EmailIcon fontSize="small" color="disabled" />}
              value={profile?.email}
            />
            <InfoRow
              icon={<PhoneIcon fontSize="small" color="disabled" />}
              value={phone}
            />
            <InfoRow
              icon={<WcIcon fontSize="small" color="disabled" />}
              value={gender}
            />
            <InfoRow
              icon={<CalendarIcon fontSize="small" color="disabled" />}
              value={formattedDob}
            />
            <InfoRow
              icon={<HomeIcon fontSize="small" color="disabled" />}
              value={address}
            />
          </Stack>
        </Box>
      </Stack>
    </>
  );
};

export default AccountSummaryCard;
